import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Mail, Phone } from "lucide-react";
import { COMPANY, COURSE_GROUPS, SECTORS, SERVICES, WHY_EUROPEDU } from "@/lib/brand";

export default function Home() {
  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden bg-[#0f172a] text-white">
        <img src="https://images.pexels.com/photos/27757151/pexels-photo-27757151.jpeg"
          alt="London skyline at sunset" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 py-24 md:py-32">
          <p className="overline text-sky-300">{COMPANY.name} · United Kingdom</p>
          <h1 className="mt-5 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] reveal">
            {COMPANY.tagline}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-200">
            EuropEdu connects talented individuals with job opportunities and guides students towards
            international educational experiences. One account works across every service, and you enter
            your information once and reuse it with your consent.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/jobs" data-testid="hero-jobs-cta" className="btn btn-primary bg-white text-[#0f172a] hover:bg-slate-200">
              Search vacancies <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/sponsors" data-testid="hero-sponsors-cta" className="btn btn-ghost border-white/40 text-white hover:text-white hover:border-white">
              Search the sponsor register
            </Link>
            <Link to="/consultations" data-testid="hero-consultation-cta" className="btn btn-ghost border-white/40 text-white hover:text-white hover:border-white">
              Book a consultation
            </Link>
          </div>
          <p className="mt-10 max-w-2xl text-xs text-slate-300">
            No EuropEdu service guarantees employment, sponsorship, admission, a Certificate of Sponsorship or a visa.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-20">
        <p className="overline">About EuropEdu</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold max-w-3xl">Why EuropEdu</h2>
        <p className="mt-4 max-w-3xl text-sm md:text-base text-slate-600">{COMPANY.intro}</p>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {WHY_EUROPEDU.map((w, i) => (
            <div key={w.title} className="card p-6 reveal" style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`why-card-${i}`}>
              <BadgeCheck className="w-5 h-5 text-[#0ea5e9]" />
              <h3 className="mt-4 text-lg font-semibold">{w.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 py-20">
          <p className="overline">Our services</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold">Everything in one platform</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Link key={s.title} to={s.to} data-testid={`service-card-${i}`} className="card p-6 bg-white reveal"
                style={{ animationDelay: `${i * 50}ms` }}>
                <h3 className="text-base font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.text}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1e3a8a]">
                  Open <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-20 grid gap-12 lg:grid-cols-2 items-start">
        <div>
          <p className="overline">Courses we provide</p>
          <h2 className="mt-3 text-3xl font-semibold">Preparation and professional programmes</h2>
          <div className="mt-8 space-y-5">
            {COURSE_GROUPS.map((g) => (
              <div key={g.title} className="card p-5" data-testid={`course-group-${g.title.replace(/ /g, "-")}`}>
                <h3 className="font-semibold">{g.title}</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {g.items.map((i) => <span key={i} className="pill bg-blue-50 text-blue-800">{i}</span>)}
                </div>
              </div>
            ))}
          </div>
          <Link to="/academy" className="btn btn-primary mt-6" data-testid="courses-cta">View the academy</Link>
        </div>
        <div>
          <p className="overline">Our sectors</p>
          <h2 className="mt-3 text-3xl font-semibold">Where we recruit</h2>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {SECTORS.map((s) => (
              <li key={s} className="card p-4 text-sm font-medium" data-testid={`sector-${s.replace(/ /g, "-")}`}>{s}</li>
            ))}
          </ul>
          <img src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b" alt="Nurse at work"
            className="mt-8 rounded-2xl w-full max-h-[80vh] md:max-h-72 object-cover" />
        </div>
      </section>

      <section className="bg-[#0f172a] text-white">
        <div className="max-w-7xl mx-auto px-5 py-16 grid gap-8 md:grid-cols-3 items-center">
          <div className="md:col-span-2">
            <p className="overline text-sky-300">Get in touch</p>
            <h2 className="mt-3 text-3xl font-semibold">Speak to a EuropEdu consultant</h2>
            <p className="mt-3 text-sm text-slate-300 max-w-xl">
              All consultations are paid sessions. Book a time, pay securely and receive written follow-up
              where your session includes it.
            </p>
            <Link to="/consultations" className="btn btn-primary bg-white text-[#0f172a] hover:bg-slate-200 mt-6"
              data-testid="contact-consultation-cta">Book a consultation</Link>
          </div>
          <div className="text-sm space-y-3">
            <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-sky-300" /> {COMPANY.email}</p>
            {COMPANY.phones.map((p) => (
              <p key={p} className="flex items-center gap-2"><Phone className="w-4 h-4 text-sky-300" /> {p}</p>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
